import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  getGreenOutcomeValueId,
  getOutcomeResultForValueId,
} from "@repo/app-types/entities/outcome";
import { outcomeColorSchemeMap } from "@repo/ui/Charts/analyticsColors";
import type { StackedBarSegment } from "@repo/ui/Charts/stackedBar";

export interface ResolvedSegment {
  label: string;
  category: string;
  count: number;
  value: string;
}

/**
 * Resolve raw `{outcomeValue, count}` entries against an outcome definition,
 * dropping values that don't belong to the definition and sorting by outcome
 * category (green → yellow → red → gray). Because it drops foreign values,
 * passing entries that include other outcome definitions' values (e.g. from a
 * ClickHouse `mapValues(outcomes)` expansion) naturally narrows to just this
 * definition's segments.
 */
export function resolveSegments(
  entries: readonly { outcomeValue: string; count: number }[],
  outcomeDef: OutcomeDefinition,
): ResolvedSegment[] {
  const resolved: ResolvedSegment[] = [];
  for (const entry of entries) {
    const result = getOutcomeResultForValueId(outcomeDef, entry.outcomeValue);
    if (!result) {
      continue;
    }
    resolved.push({
      label: result.label,
      category: result.category,
      count: entry.count,
      value: result.value,
    });
  }

  const order = ["green", "yellow", "red", "gray"];
  return resolved.sort(
    (a, b) => order.indexOf(a.category) - order.indexOf(b.category),
  );
}

/**
 * Green (positive) outcome rate over the passed segments, i.e. green count
 * divided by the total of all supplied segments. Returns `null` when there is
 * no green value or no data. Filter out N/A segments before calling to get the
 * canonical win rate (green / applicable).
 */
export function computeGreenRate(
  segments: readonly ResolvedSegment[],
  outcomeDef: OutcomeDefinition,
): number | null {
  const greenValue = getGreenOutcomeValueId(outcomeDef);
  if (!greenValue) {
    return null;
  }

  const total = segments.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) {
    return null;
  }

  const greenCount = segments.find((s) => s.value === greenValue)?.count ?? 0;

  return greenCount / total;
}

/**
 * Map resolved outcome segments to `StackedBar` segments, coloring each by its
 * outcome category and sizing its width as a share of `total`.
 */
export function toBarSegments(
  segments: readonly ResolvedSegment[],
  total: number,
): StackedBarSegment[] {
  return segments.map((seg) => {
    const scheme = outcomeColorSchemeMap[seg.category];
    return {
      color: scheme?.fill ?? "var(--color-gray-300)",
      strokeColor: scheme?.stroke ?? "var(--color-gray-400)",
      name: seg.label,
      count: seg.count,
      percent: total === 0 ? 0 : (seg.count / total) * 100,
    };
  });
}
